import { useMemo } from 'react'
import { Descendant } from 'slate'

import {
  ConditionTextOperator,
  GalleryModal,
  ImageArrayValue,
  TypographyGroupValue,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import {
  BaseComponent,
  CornerRadius,
  Gallery,
  GalleryType,
  Ratio,
  RawSpacingInput,
  Skeleton,
  SkeletonGroup,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { ImageArrayCell } from '@/core/models/columns/imageArray'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey } from '@/shared/builder'
import { AspectRatio } from '@/shared/builder/types'
import { getSafely } from '@/shared/safely'

import { CompHeadline } from '../../shared/CompHeadline'
import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'

export interface CompArrayGalleryWrapper {
  infos: CoreComponentInfos
  tableData: CoreComponentTableData
  setGalleryModal: (galleryModal: GalleryModal) => void
}

/**
 * @todo:
 * - fix carousel layout become blank when switching number of items
 */
export function CompArrayGalleryWrapperCore({
  infos: { component, settings, containerClient, detailTableId, rowId, detailRowIndex, currentParentBg },
  tableData: { dbTable: table },
  setGalleryModal,
}: CompArrayGalleryWrapper) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================

  // columns
  const imageArrayCol = settings?.[ComponentSettingKey.ArrayGalleryDataImage] as string | undefined

  // get current rows, filter by current rowId automatically
  const [{ rows, filteredRowsCount }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    pagination: { page: 0, perPage: 1 },
    filterSetting: {
      isAny: false,
      conditions: [
        {
          lhs: '_id',
          operator: ConditionTextOperator.IsEqual,
          caseSensitive: false,
          userSpecific: false,
          rhs: {
            id: 'current-row',
            flag: VariableSelectFeatureFlag.CustomText,
            value: rowId as string,
          },
        },
      ],
    },
  })
  const currentRow = rows[0]

  // max items limit
  const numItems = (settings?.[ComponentSettingKey.ArrayGalleryDataNumArrayItems] as number) ?? 10
  const maxItems = !isNaN(Number(numItems)) ? Number(numItems) : 10

  // prepare image array
  const imageArray = useMemo(() => {
    if (!currentRow || !imageArrayCol) return []

    const imageArrayCell = currentRow?.[imageArrayCol] as ImageArrayCell
    const imageArrayValues = (imageArrayCell?.value ?? []) as unknown as ImageArrayValue[]
    const imagesAfterLimit = imageArrayValues.slice(0, maxItems)

    return imagesAfterLimit
    //
  }, [currentRow, imageArrayCol, maxItems])

  // ================================= design ================================

  // base
  const width = getSafely(settings, [ComponentSettingKey.ArrayGalleryDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.ArrayGalleryDesignCompHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.ArrayGalleryDesignMargin] as RawSpacingInput
  const modalWidth = getSafely(settings, [ComponentSettingKey.ArrayGalleryDesignModalWidth], ['100%'])[0]

  // array-gallery
  const layout = settings?.[ComponentSettingKey.ArrayGalleryDesignLayout] as GalleryType
  const columnsCount = settings?.[ComponentSettingKey.ArrayGalleryDesignNumCols]
  const showTitle = !Boolean(settings?.[ComponentSettingKey.ArrayGalleryDesignHideTitle])
  const carouselArrow = Boolean(settings?.[ComponentSettingKey.ArrayGalleryDesignCarouselArrow])
  const carouselIndicator = Boolean(settings?.[ComponentSettingKey.ArrayGalleryDesignCarouselIndicator])
  const imageCornerRadius = settings?.[ComponentSettingKey.ArrayGalleryDesignImageShape] as CornerRadius
  const avatarCornerRadius = settings?.[ComponentSettingKey.ArrayGalleryDesignAvatarShape] as CornerRadius
  const aspectRatio =
    layout !== 'masonry'
      ? (settings?.[ComponentSettingKey.ArrayGalleryDesignAspectRatio] as AspectRatio)
      : AspectRatio.SixteenByNine
  const ratio = aspectRatio.replace(':', '/') as Ratio
  const typoSettings = settings?.[ComponentSettingKey.ArrayGalleryDesignTypography] as TypographyGroupValue
  const captionTypography = typoSettings?.[ComponentSettingKey.ArrayGalleryDesignCaptionTypography]
  const galleryGap = settings?.[ComponentSettingKey.ArrayGalleryDesignGap] as RawSpacingInput
  const carouselBackgroundColor = settings?.[ComponentSettingKey.ArrayGalleryDesignCarouselBackgroundColor]

  // ========================= component title / headline =========================
  const headline = (settings?.[ComponentSettingKey.ArrayGalleryDataCompTitle] as Descendant[]) ?? []
  const headlineMargin = settings?.[ComponentSettingKey.ArrayGalleryDesignHeadlineMargin]
  const headlineTypo = typoSettings?.[ComponentSettingKey.ArrayGalleryDesignHeadlineTypography]
  const compTitleAlign = settings[ComponentSettingKey.ArrayGalleryDesignCompTitleHAlign] ?? 'parent'
  // const compTitleColor = settings[ComponentSettingKey.ArrayGalleryDesignComponentTitleColor] ?? 'on-primary'

  // ================================= derived ================================

  // ================================= action ================================

  const handleImageClick = (image: ImageArrayValue, idx: number) => {
    const galleryModal: GalleryModal = {
      src: image.Url,
      title: image.Title || '',
      description: '', // hide the caption for now as requested by Merav
      tableId: detailTableId!,
      colId: '',
      userSpecific: false,
      rowIndex: detailRowIndex!,
      itemIndex: idx,
      aspectRatio: aspectRatio,
      width: modalWidth,
    }

    setGalleryModal(galleryModal)
  }

  // ================================= render ================================

  const shouldShow = imageArray.length > 0
  if (!shouldShow) return null

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const renderItems = () => {
    return imageArray.map((image) => {
      return {
        src: image.Url,
        alt: image.Title || '',
        ratio,
        cornerRadius: imageCornerRadius,
        onClick: () => handleImageClick(image, 0),
      }
    })
  }

  const render = (
    <BaseComponent
      id={component._id}
      width={width}
      alignment={alignment}
      margin={margin}
      className='relative box-border flex flex-col'
    >
      <CompHeadline
        containerClient={containerClient}
        detailRowIndex={detailRowIndex}
        detailTableId={detailTableId}
        headline={headline}
        headlineMargin={headlineMargin}
        headlineTypo={headlineTypo}
        compTitleAlign={compTitleAlign}
        backgroundColor={currentParentBg}
        colorType='adaptive'
      />

      {isLoading ? (
        <SkeletonGroup backgroundColor={currentParentBg!}>
          <div className='grid gap-4' style={{ gridTemplateColumns: `repeat(${columnsCount}, 1fr)` }}>
            {[...new Array(columnsCount * Math.min(filteredRowsCount, 2))].map((_, i) => (
              <Skeleton
                key={i}
                className='w-full rounded-md'
                style={{ aspectRatio: (ratio as any) === 'auto' ? '1/1' : ratio }}
              />
            ))}
          </div>
        </SkeletonGroup>
      ) : (
        <Gallery
          type={layout}
          images={renderItems()}
          columns={columnsCount}
          captionTypography={captionTypography.typography}
          pageBackgroundColor={currentParentBg}
          gap={galleryGap as number}
          carouselProps={{
            showNavigation: carouselArrow,
            showDots: carouselIndicator,
            loop: true,
            backgroundColor: currentParentBg,
          }}
        />
      )}
    </BaseComponent>
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {render}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return render
}
